// Stage 4's two logs: every order the player issued with its real timings, and the decision log — each
// significant event with what blue did in the next five minutes and what red decided in the same
// window, red's reason strings revealed (spec §2 Stage 4).

import { useMemo } from 'react';
import { decisionLog } from '../../debrief/analyze';
import type { Grid } from '../../sim/grid';
import type { Event, Graphic, Order, Unit } from '../../sim/types';
import { useDebrief } from '../../state/debrief';
import { clockHM } from '../reports';
import { cardLabel, doneTicks, eventLine, orderClock, targetSummary } from './format';

const STATUS_TONE: Record<string, string> = {
  done: 'var(--color-isr)',
  executing: 'var(--color-friendly)',
  rejected: 'var(--color-hostile)',
  cancelled: 'var(--color-fog)',
};

export function OrderLogTable({
  orders, events, units, graphics, grid,
}: {
  orders: Order[];
  events: Event[];
  units: Unit[];
  graphics: Graphic[];
  grid: Grid | null;
}) {
  const blue = useMemo(
    () => orders.filter((o) => o.side === 0).sort((a, b) => a.issuedAt - b.issuedAt || a.id - b.id),
    [orders],
  );
  const done = useMemo(() => doneTicks(blue, events), [blue, events]);
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);

  return (
    <section data-testid="order-log" className="wt-panel p-3">
      <Heading>Order log</Heading>
      <p className="mt-1 text-[11px] leading-snug text-fog">
        Every card you played, with the time it went on the command net, the time it reached the unit
        after the C2 (command and control) delay, the time the unit started acting, and the time it
        finished — all on the one clock, H+hours:minutes:seconds from H-hour. The gap between the first
        and third of those is the price of the order.
      </p>
      {blue.length === 0 ? (
        <p className="mono mt-3 text-[11px] text-fog">No orders were issued in this run.</p>
      ) : (
        <div className="thin-scroll mt-2 max-h-[320px] overflow-auto">
          <table className="mono w-full border-collapse text-[11px]">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="text-left text-[9px] uppercase tracking-[0.14em] text-fog">
                <Th>Callsign</Th><Th>Card</Th><Th>Target</Th>
                <Th title="When the order went on the command net">Issue</Th>
                <Th title="When the order reached the unit">Transmit</Th>
                <Th title="When the unit began executing">Execute</Th>
                <Th title="When the unit reported complete">Done</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {blue.map((o) => {
                const doneAt = done.get(o.id);
                return (
                  <tr key={o.id} data-testid="order-row" className="border-t border-slate-800">
                    <Td className="text-paper">{unitById.get(o.unitId)?.callsign ?? `UNIT ${o.unitId}`}</Td>
                    <Td className="text-paper">{cardLabel(o.card)}</Td>
                    <Td>{targetSummary(o.target, graphics, units, grid)}</Td>
                    <Td>{orderClock(o.issuedAt)}</Td>
                    <Td>{orderClock(o.txDone)}</Td>
                    <Td>{orderClock(o.execStart)}</Td>
                    <Td>{doneAt === undefined ? '—' : orderClock(doneAt)}</Td>
                    <Td style={{ color: STATUS_TONE[o.status] ?? 'var(--color-fog)' }}>
                      {o.status}
                      {o.reason ? ` — ${o.reason}` : ''}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function DecisionLog({
  events, orders, units, graphics, grid,
}: {
  events: Event[];
  orders: Order[];
  units: Unit[];
  graphics: Graphic[];
  grid: Grid | null;
}) {
  const entries = useMemo(() => decisionLog(events, orders), [events, orders]);
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);
  const scrubToEvent = useDebrief((s) => s.scrubToEvent);

  return (
    <section data-testid="decision-log" className="wt-panel p-3">
      <Heading>Decision log</Heading>
      <p className="mt-1 text-[11px] leading-snug text-fog">
        Each significant event on <em>your</em> side of the fight, what you ordered in the five minutes
        that followed, and what the enemy commander did in the same window. The enemy's reasoning was
        hidden during the fight; it is shown here in full, in its own column — a red event in the left
        column would read as you standing idle, which is not what it was.
      </p>
      <div className="thin-scroll mt-2 max-h-[460px] space-y-2 overflow-auto pr-1">
        {entries.length === 0 && <p className="mono text-[11px] text-fog">No significant events were logged.</p>}
        {entries.map((entry) => (
          <article
            key={entry.event.id}
            data-testid="decision-entry"
            className="wt-card"
            style={{ borderLeft: '2px solid var(--color-warn)' }}
          >
            <button
              type="button"
              onClick={() => scrubToEvent(entry.event.t)}
              title="Scrub the replay to this moment"
              className="mono block w-full text-left text-[11px] text-paper hover:text-[color:var(--color-friendly)]"
            >
              <span className="text-fog">{clockHM(entry.event.t)}</span>{' '}
              {eventLine(entry.event, units, grid)}
              {entry.alsoCrossed && entry.alsoCrossed.length > 0 && (
                // One row per phase line, not one per platoon: the other callsigns that crossed it
                // hang off the first crossing instead of each getting a row of "nothing ordered".
                <span className="text-fog">
                  {' '}— with {entry.alsoCrossed.map((id) => unitById.get(id)?.callsign ?? `UNIT ${id}`).join(', ')}
                </span>
              )}
            </button>

            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <div>
                <div className="mono text-[9px] uppercase tracking-[0.14em] text-fog">
                  Your orders within 5 min
                </div>
                {entry.blueOrders.length === 0 ? (
                  <div className="mono mt-1 text-[11px] text-fog opacity-60">nothing ordered</div>
                ) : (
                  <ul className="mt-1 space-y-0.5">
                    {entry.blueOrders.map((o) => (
                      <li key={o.id} className="mono text-[11px] text-paper">
                        <span className="text-fog">{clockHM(o.issuedAt)}</span>{' '}
                        {unitById.get(o.unitId)?.callsign ?? `UNIT ${o.unitId}`} — {cardLabel(o.card)}{' '}
                        <span className="text-fog">{targetSummary(o.target, graphics, units, grid)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <div className="mono text-[9px] uppercase tracking-[0.14em]" style={{ color: 'var(--color-hostile)' }}>
                  Red cell reasoning — revealed
                </div>
                {entry.redDecisions.length === 0 ? (
                  <div className="mono mt-1 text-[11px] text-fog opacity-60">the enemy did nothing new</div>
                ) : (
                  <ul className="mt-1 space-y-0.5">
                    {entry.redDecisions.map((r) => (
                      <li key={r.id} className="mono text-[11px] leading-snug text-paper">
                        <span className="text-fog">{clockHM(r.t)}</span>{' '}
                        {r.kind === 'red.decision'
                          ? String(r.payload.reason ?? r.payload.playId ?? 'decision')
                          : eventLine(r, units, grid)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mono text-[12px] uppercase tracking-[0.22em] text-paper">{children}</h2>
  );
}

function Th({ children, title }: { children: React.ReactNode; title?: string }) {
  return <th title={title} className="whitespace-nowrap px-1.5 py-1 font-normal">{children}</th>;
}

function Td({ children, className = '', style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return <td className={`whitespace-nowrap px-1.5 py-1 text-fog ${className}`} style={style}>{children}</td>;
}
